import React from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import Badge from '@atlaskit/badge/badge';
import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { HierarchyPerson } from '../../../types';

const styles = cssMap({
	row: {
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		backgroundColor: token('color.background.neutral'),
		width: '100%',
	},
	selectedRow: {
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border.selected'),
	},
});

interface MiniHierarchyRowProps {
	person: HierarchyPerson;
	/** Marks the profiled user's row so it is visually highlighted as the anchor of the line. */
	selected?: boolean;
	rowRef?: React.RefObject<HTMLDivElement>;
	testId?: string;
}

/**
 * A single, static row in the reporting-line column: avatar, name, job title, and an optional
 * total-reports badge. Rows are never interactive (the reporting-line view is read-only).
 */
export function MiniHierarchyRow({
	person,
	selected = false,
	rowRef,
	testId,
}: MiniHierarchyRowProps): React.JSX.Element {
	const { name, avatarUrl, jobTitle, numberOfTotalReports = 0, numberOfDirectReports = 0 } = person;
	const showBadge = numberOfTotalReports > 0 && numberOfDirectReports > 0;

	return (
		<Box ref={rowRef} xcss={cx(styles.row, selected && styles.selectedRow)} testId={testId}>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Inline space="space.150" alignBlock="center">
					<Avatar src={avatarUrl ?? undefined} name={name} size="small" />
					<Stack>
						<Text size="small" color="color.text" weight="medium" maxLines={1}>
							{name}
						</Text>
						{jobTitle && (
							<Text size="small" color="color.text.subtlest" maxLines={1}>
								{jobTitle}
							</Text>
						)}
					</Stack>
				</Inline>
				{showBadge && (
					<Badge appearance={selected ? 'primary' : 'default'} max={99999}>
						{numberOfTotalReports}
					</Badge>
				)}
			</Inline>
		</Box>
	);
}
